import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import logo from "../assets/images/White_Version.png";
import { Boxes, DollarSign, Percent, Truck } from "lucide-react";
import { getAuthCookie } from "../utils/authCookies";

/** Base URL for Studio (new tab) — same as SignageSidebar. */
function getStudioExternalUrl(): string {
  return (process.env.REACT_APP_STUDIO_BASE_URL || "").trim();
}

// Module codes from /permission-scope/modules API
type ModuleCode =
  | "SHIPMENTS"
  | "CUSTOMER_GROUPS"
  | "BROKERAGE_TEAM_MEMBERS"
  | "USERS"
  | "BRANDS"
  | "OFFICE_INCOMING_PRODUCTS"
  | "AGENT_INCOMING_PRODUCTS"
  | "DISCOUNTS"
  | "ATTRIBUTES"
  | "REMOVAL_ORDERS"
  | "CATEGORIES"
  | "DYNAMIC_COSTS"
  | "ORDERS"
  | "BROKERAGE_AGENTS"
  | "REPORTS"
  | "DASH_BOARD"
  | "SIGNAGE_TEAM_MEMBERS"
  | "ASSETS"
  | "POSTAGE_WALLET"
  | "ACTIVE_INSTALLATIONS"
  | "INSTALLATION_ORDERS"
  | "OFFICE_INVENTORY"
  | "BATCH_PAYMENTS"
  | "OVERDUE_TRIGGER_CONFIGURATION"
  | "INTEGRATIONS"
  | "PAYMENT_METHODS"
  | "SPECIAL_FEES"
  | "BUY_LABELS"
  | "STANDARD_INVENTORY"
  | "AGENT_INVENTORY"
  | "SHIPPING_PRESETS"
  | "AGENTS"
  | "SHIPPING_METHODS_COST"
  | "BROKERAGE_FIRMS"
  | "INVOICE_TEMPLATES"
  | "EMAIL_TEMPLATE"
  | "PROJECTS"
  | "PRODUCTION_OVERVIEW"
  | "ROLES"
  | "POST_TYPE"
  | "POST_TYPES"
  | "MARKETPLACE_PROFILE"
  | "INCOMING_PRODUCTS"
  | "LINKED_INSTALLER"
  | "TAX_SETTINGS"
  | "SETTINGS"
  | "GENERAL_SETTINGS"
  | "WAREHOUSES"
  | "STUDIO"
  | "DESIGNS"
  | "BROKERAGE_CHANGE_REQUESTS"
  | (string & {});

type ItemMeta = { label: string; path: string; icon?: string };

// Helper function to create path maps based on baseRoute
const createSingleMap = (baseRoute: string): Record<string, ItemMeta> => ({
  AGENTS: { label: "Agents", path: `${baseRoute}/agents`, icon: "ri-user-3-line" },
  CUSTOMER_GROUPS: { label: "Groups", path: `${baseRoute}/groups`, icon: "ri-team-fill" },
  BROKERAGE_FIRMS: { label: "Brokerage Firms", path: `${baseRoute}/brokerageFirms`, icon: "bx bxs-business" },
  BROKERAGE_TEAM_MEMBERS: {
    label: "Brokerage Teammembers",
    path: `${baseRoute}/brokerageTeammembers`,
    icon: "ri-team-line",
  },
  POST_TYPE: { label: "Post Types", path: `${baseRoute}/posttypes`, icon: "lucide-boxes" },
  POST_TYPES: { label: "Post Types", path: `${baseRoute}/posttypes`, icon: "lucide-boxes" },
  ASSETS: { label: "Assets", path: `${baseRoute}/rotationalProducts`, icon: "ri-store-2-line" },
  ORDERS: { label: "Orders", path: `${baseRoute}/orders`, icon: "bx bx-cart-alt" },
  BATCH_PAYMENTS: { label: "Batch Payments", path: `${baseRoute}/batchPayments`, icon: "ri-money-dollar-circle-line" },
  INTEGRATIONS: { label: "Integrations", path: `${baseRoute}/integrations`, icon: "ri-file-settings-fill" },
  PAYMENT_METHODS: { label: "Payment Methods", path: `${baseRoute}/paymentMethods`, icon: "ri-bank-card-2-line" },
  SPECIAL_FEES: { label: "Special Fee", path: `${baseRoute}/specialfees`, icon: "dollar-sign" },
  DISCOUNTS: { label: "Discounts", path: `${baseRoute}/SignageDiscounts`, icon: "percent" },
  MARKETPLACE_PROFILE: { label: "Marketplace Profile", path: `${baseRoute}/marketplace_profile`, icon: "ri-profile-line" },
  PROJECTS: { label: "Projects", path: `${baseRoute}/projects`, icon: "ri-folder-chart-line" },
});

// Helper: Inventory sub-section (Standard, Agent, Office) - order matches SignageSidebar
const createInventorySubChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  STANDARD_INVENTORY: { label: "Standard Inventory", path: `${baseRoute}/products` },
  AGENT_INVENTORY: { label: "Agent Inventory", path: `${baseRoute}/agentInventory` },
  OFFICE_INVENTORY: { label: "Office Inventory", path: `${baseRoute}/officeInventory` },
});
// Helper: Catalog Setup sub-section (Categories, Brands, Attributes)
const createCatalogSetupSubChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  CATEGORIES: { label: "Categories", path: `${baseRoute}/categories` },
  BRANDS: { label: "Brands", path: `${baseRoute}/brands` },
  ATTRIBUTES: { label: "Attributes", path: `${baseRoute}/attributes` },
});

// Helper function to create jobs children based on baseRoute (order matches SignageSidebar)
const createJobsChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  INSTALLATION_ORDERS: { label: "Installations", path: `${baseRoute}/jobs/installations` },
  ACTIVE_INSTALLATIONS: { label: "Active Installations", path: `${baseRoute}/active-installations` },
  REMOVAL_ORDERS: { label: "Removal Requests", path: `${baseRoute}/jobs/removalRequests` },
  PRODUCTION_OVERVIEW: { label: "Production Overview", path: `${baseRoute}/productionOverview` },
});

// Helper function to create incoming children based on baseRoute
const createIncomingChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  AGENT_INCOMING_PRODUCTS: { label: "Agent Products", path: `${baseRoute}/incomingAgentProducts`, icon: "ri-user-line" },
  OFFICE_INCOMING_PRODUCTS: { label: "Office Products", path: `${baseRoute}/incomingOfficeProducts`, icon: "ri-user-line" }
});

// Helper function to create tax children based on baseRoute
const createTaxChildren = (baseRoute: string): ItemMeta[] => [
  { label: "Geo Locations", path: `${baseRoute}/geoLocations`, icon: "ri-user-line" },
  { label: "Tax Rates", path: `${baseRoute}/taxRates`, icon: "ri-user-line" },
  { label: "Tax Rules", path: `${baseRoute}/taxRules`, icon: "ri-user-line" },
  { label: "Tax Groups", path: `${baseRoute}/taxGroups`, icon: "ri-user-line" },
];

// Helper function to create reports children based on baseRoute
const createReportsChildren = (baseRoute: string): ItemMeta[] => [
  { label: "Installer Jobs", path: `${baseRoute}/installerJobReport` },
  { label: "Installations Billing & Summary", path: `${baseRoute}/installationSummaryReport` },
  { label: "Removal Billing & Summary", path: `${baseRoute}/removalSummaryReport` },
  { label: "SalesOrder Summary", path: `${baseRoute}/salesOrderReport` },
];

// Settings child - structure matches SignageSidebar (Tax Settings has subchildren)
type SettingsChildMeta = ItemMeta & { moduleCode?: ModuleCode; subchildren?: ItemMeta[] };
const createSettingsChildren = (baseRoute: string): SettingsChildMeta[] => [
  { label: "General", path: `${baseRoute}/generalSettings`, moduleCode: "GENERAL_SETTINGS" },
  { label: "Invoice Templates", path: `${baseRoute}/InvoiceTemplates`, moduleCode: "INVOICE_TEMPLATES" },
  {
    label: "Email Templates",
    path: `${baseRoute}/SignageemailTeampleates`,
    moduleCode: "EMAIL_TEMPLATE",
  },
  { label: "Dynamic Costs", path: `${baseRoute}/dynamic-costs`, moduleCode: "DYNAMIC_COSTS" },
  { label: "Migrations", path: `${baseRoute}/migrations` ,moduleCode:"GENERAL_SETTINGS"},
  {
    label: "Tax Settings",
    path: `${baseRoute}/taxRates`,
    moduleCode: "TAX_SETTINGS",
    subchildren: [
      { label: "Geo Locations", path: `${baseRoute}/geoLocations` },
      { label: "Tax Rates", path: `${baseRoute}/taxRates` },
      { label: "Tax Rules", path: `${baseRoute}/taxRules` },
      { label: "Tax Groups", path: `${baseRoute}/taxGroups` },
    ],
  },
  { label: "Overdue Trigger Settings", path: `${baseRoute}/overdueSettings`, moduleCode: "OVERDUE_TRIGGER_CONFIGURATION" },
];

// Helper function to create shipments children based on baseRoute (order matches SignageSidebar)
const createShipmentsChildren = (baseRoute: string): Record<string, ItemMeta> => ({
  SHIPPING_PRESETS: { label: "Shipping Presets", path: `${baseRoute}/shippingpresets` },
  BUY_LABELS: { label: "Buy Label", path: `${baseRoute}/buylabels` },
  POSTAGE_WALLET: { label: "Postage Wallet", path: `${baseRoute}/postagewallet` },
  SHIPPING_METHODS_COST: { label: "Shipping Cost", path: `${baseRoute}/shipping/shipping-costs` },
});

type TeammemberProfile = {
  firstName: string;
  lastName: string;
  profilePic: string;
  jobTitle: string;
};

export default function SignageTeammemberSideBar() {
  const location = useLocation();
  const [codes, setCodes] = useState<ModuleCode[]>([]);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken") || "";
  const membershipId = getAuthCookie("membershipId") || "";
  const userName = getAuthCookie("userName") || "";
  const [memberProfile, setMemberProfile] = useState<TeammemberProfile | null>(null);

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // Create dynamic path maps based on baseRoute
  const SINGLE_MAP = useMemo(() => createSingleMap(baseRoute), [baseRoute]);
  const INVENTORY_SUB_CHILDREN = useMemo(() => createInventorySubChildren(baseRoute), [baseRoute]);
  const CATALOG_SETUP_SUB_CHILDREN = useMemo(() => createCatalogSetupSubChildren(baseRoute), [baseRoute]);
  const JOBS_CHILDREN = useMemo(() => createJobsChildren(baseRoute), [baseRoute]);
  const INCOMING_CHILDREN = useMemo(() => createIncomingChildren(baseRoute), [baseRoute]);
  const TAX_CHILDREN = useMemo(() => createTaxChildren(baseRoute), [baseRoute]);
  const REPORTS_CHILDREN = useMemo(() => createReportsChildren(baseRoute), [baseRoute]);
  const SETTINGS_CHILDREN = useMemo(() => createSettingsChildren(baseRoute), [baseRoute]);
  const SHIPMENTS_CHILDREN = useMemo(() => createShipmentsChildren(baseRoute), [baseRoute]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!membershipId || !token) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organization-memberships/${membershipId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        setMemberProfile({
          firstName: String(data?.firstName || "").trim(),
          lastName: String(data?.lastName || "").trim(),
          profilePic: String(data?.profilePic || "").trim(),
          jobTitle: String(data?.jobTitle || "").trim(),
        });
      } catch (e) {
        console.error("Failed to fetch team member profile:", e);
        if (!cancelled) setMemberProfile(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [membershipId, token]);

  // fetch allowed module codes (use your working path)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/permission-scope/modules`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error("Invalid response");
        if (!cancelled) setCodes((data.filter((x: any) => typeof x === "string") as string[]) as ModuleCode[]);
      } catch (e) {
        console.error("Failed to fetch permission-scope modules:", e);
        if (!cancelled) setCodes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.innerWidth < 992) {
      document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
    }
  }, [location.pathname]);

  const has = (code: ModuleCode) => codes.includes(code);
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  // Parent stays open when this menu OR any nested child is open (fixes child/nested child closing parent)
  const CHILD_MAP: Record<string, string[]> = {
    productsMenu: ["inventorySub", "catalogSub"],
    configMenu: ["myOrgSub", "settingsSub", "taxSub"],
    settingsSub: ["taxSub"],
  };
  const isParentOpen = (parentId: string) =>
    openMenu === parentId || (CHILD_MAP[parentId]?.includes(openMenu ?? "") ?? false);

  // build group children - Inventory (Standard, Agent, Office) and Catalog Setup (Categories, Brands, Attributes)
  const inventorySubChildren = useMemo(
    () =>
      (Object.keys(INVENTORY_SUB_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => INVENTORY_SUB_CHILDREN[code]),
    [codes, INVENTORY_SUB_CHILDREN]
  );
  const catalogSetupSubChildren = useMemo(
    () =>
      (Object.keys(CATALOG_SETUP_SUB_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => CATALOG_SETUP_SUB_CHILDREN[code]),
    [codes, CATALOG_SETUP_SUB_CHILDREN]
  );
  const showProductsInventory =
    inventorySubChildren.length > 0 ||
    catalogSetupSubChildren.length > 0 ||
    has("ASSETS") ||
    has("WAREHOUSES");

  const jobsChildren = useMemo(
    () =>
      (Object.keys(JOBS_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => JOBS_CHILDREN[code]),
    [codes]
  );

  const incomingChildren = useMemo(
    () =>
      (Object.keys(INCOMING_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => INCOMING_CHILDREN[code]),
    [codes]
  );

  // Settings: backend does not send SETTINGS module - always show dropdown (default), filter children by their module codes
  const settingsChildren = useMemo(
    () =>
      SETTINGS_CHILDREN.filter(
        (child) => !child.moduleCode || has(child.moduleCode as ModuleCode)
      ),
    [codes, SETTINGS_CHILDREN]
  );
  const showSettings = settingsChildren.length > 0; // No SETTINGS module from backend - show when user has any settings child permission

  const showIncoming = has("INCOMING_PRODUCTS") || has("LINKED_INSTALLER") || incomingChildren.length > 0;
  const showTax = has("TAX_SETTINGS");

  const shipmentsChildren = useMemo(
    () =>
      (Object.keys(SHIPMENTS_CHILDREN) as ModuleCode[])
        .filter((code) => has(code))
        .map((code) => SHIPMENTS_CHILDREN[code]),
    [codes, SHIPMENTS_CHILDREN]
  );
  const showShipments = shipmentsChildren.length > 0;

  const studioUrl = getStudioExternalUrl();
  const hasStudioModule = has("STUDIO");
  /** External "Studio" row only when URL configured */
  const showStudioExternal = hasStudioModule && !!studioUrl;
  /** Designs whenever STUDIO is allowed (same as SignageSidebar grouping), or DESIGNS-only if API sends it */
  const showDesignsLink = hasStudioModule || has("DESIGNS");
  const showStudioMenu = hasStudioModule || has("DESIGNS");

  const sidebarDisplayName =
    [memberProfile?.firstName, memberProfile?.lastName].filter(Boolean).join(" ").trim() ||
    userName ||
    "Team Member";

  return (
    <div className="app-menu navbar-menu">
      {/* ✅ MOBILE top strip (logo + close), visible on ≤767px */}
      <div className="mobile-sidebar-header d-md-none d-flex align-items-center gap-2 px-3 py-2">
        <Link to="/" className="d-inline-flex align-items-center text-decoration-none">
          <img src={logo} alt="Signage Team" height={80} />
        </Link>
        <button
          type="button"
          aria-label="Close sidebar"
          className="btn btn-sm btn-icon btn-ghost-light ms-auto mt-1"
          onClick={() => {
            document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
          }}
        >
          <i className="ri-close-line fs-4" />
        </button>
      </div>
      {/* Brand */}
      <div className="navbar-brand-box d-none d-md-block">
        <Link to="/" className="logo logo-dark">
          <span className="logo-sm"><img src={logo} alt="" height="22" /></span>
          <span className="logo-lg"><img src={logo} alt="" height="40" /></span>
        </Link>

        <Link to="/" className="logo logo-light">
          <span className="logo-sm"><img src={logo} alt="" height="22" /></span>
          <span className="logo-lg"><img src={logo} alt="" height="90" style={{ marginTop: "10px" }} /></span>
        </Link>

        <button type="button" className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover" id="vertical-hover">
          <i className="ri-record-circle-line" />
        </button>
      </div>

      {/* User Dropdown */}
      <div className="dropdown sidebar-user m-1 rounded">
        <button type="button" className="btn material-shadow-none" id="page-header-user-dropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <span className="d-flex align-items-center gap-2">
            <img className="rounded header-profile-user" src="/assets/images/users/avatar-1.jpg" alt="Header Avatar" />
            <span className="text-start">
              <span className="d-block fw-medium sidebar-user-name-text">{sidebarDisplayName}</span>
              <span className="d-block fs-14 sidebar-user-name-sub-text">
                <i className="ri-circle-fill fs-10 text-success align-baseline" />
                <span className="align-middle">Online</span>
              </span>
            </span>
          </span>
        </button>
        <div className="dropdown-menu dropdown-menu-end">
          <h6 className="dropdown-header">Welcome {memberProfile?.firstName || sidebarDisplayName}!</h6>
          <Link to={`${baseRoute}/profile`} className="dropdown-item">
            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1" />
            <span className="align-middle">Profile</span>
          </Link>
          <LogoutButton />
        </div>
      </div>

      {/* Sidebar Navigation */}
      <div id="scrollbar">
        <div className="container-fluid">
          <ul className="navbar-nav" id="navbar-nav">

            {/* 1. Dashboard - order matches SignageSidebar */}
            {has("DASH_BOARD") && (
              <li className="nav-item">
                <Link to={`${baseRoute}/dashboard`} className={`nav-link menu-link ${isActive(`${baseRoute}/dashboard`) ? "active" : ""}`}>
                  <i className="ri-dashboard-fill" /><span>Dashboard</span>
                </Link>
              </li>
            )}

            {/* 2. Reports */}
            {has("REPORTS") && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "reportsMenu" ? null : "reportsMenu")} href="#reportsMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "reportsMenu" ? "true" : "false"} aria-controls="reportsMenu">
                  <i className="ri-bar-chart-2-fill" /><span>Reports</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "reportsMenu" ? "show" : ""}`} id="reportsMenu">
                  <ul className="nav nav-sm flex-column">
                    {REPORTS_CHILDREN.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* 3. Business (Brokerage Firms, Brokerage Teammembers, Agents, Groups, Brokerage Requests) */}
            {(has("BROKERAGE_FIRMS") ||
              has("BROKERAGE_TEAM_MEMBERS") ||
              has("AGENTS") ||
              has("CUSTOMER_GROUPS") ||
              has("BROKERAGE_CHANGE_REQUESTS")) && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "businessMenu" ? null : "businessMenu")} href="#businessMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "businessMenu" ? "true" : "false"} aria-controls="businessMenu">
                  <i className="bx bxs-business" /><span>Business</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "businessMenu" ? "show" : ""}`} id="businessMenu">
                  <ul className="nav nav-sm flex-column">
                    {has("BROKERAGE_FIRMS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.BROKERAGE_FIRMS.path} className={`nav-link ${isActive(SINGLE_MAP.BROKERAGE_FIRMS.path) ? "active" : ""}`}>{SINGLE_MAP.BROKERAGE_FIRMS.label}</Link>
                      </li>
                    )}
                    {has("BROKERAGE_TEAM_MEMBERS") && (
                      <li className="nav-item">
                        <Link
                          to={SINGLE_MAP.BROKERAGE_TEAM_MEMBERS.path}
                          className={`nav-link ${
                            isActive(SINGLE_MAP.BROKERAGE_TEAM_MEMBERS.path) ||
                            isActive(`${baseRoute}/brokerageTeammember`)
                              ? "active"
                              : ""
                          }`}
                        >
                          {SINGLE_MAP.BROKERAGE_TEAM_MEMBERS.label}
                        </Link>
                      </li>
                    )}
                    {has("AGENTS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.AGENTS.path} className={`nav-link ${isActive(SINGLE_MAP.AGENTS.path) ? "active" : ""}`}>{SINGLE_MAP.AGENTS.label}</Link>
                      </li>
                    )}
                    {has("CUSTOMER_GROUPS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.CUSTOMER_GROUPS.path} className={`nav-link ${isActive(SINGLE_MAP.CUSTOMER_GROUPS.path) ? "active" : ""}`}>{SINGLE_MAP.CUSTOMER_GROUPS.label}</Link>
                      </li>
                    )}
                    {has("BROKERAGE_CHANGE_REQUESTS") && (
                      <li className="nav-item">
                        <Link
                          to={`${baseRoute}/brokerageRequests`}
                          className={`nav-link ${isActive(`${baseRoute}/brokerageRequests`) ? "active" : ""}`}
                        >
                          Brokerage Requests
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* 4. Orders & Fulfillment */}
            {has("ORDERS") && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "ordersMenu" ? null : "ordersMenu")} href="#ordersMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "ordersMenu" ? "true" : "false"} aria-controls="ordersMenu">
                  <i className="bx bx-cart-alt" /><span>Orders & Fulfillment</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "ordersMenu" ? "show" : ""}`} id="ordersMenu">
                  <ul className="nav nav-sm flex-column">
                    <li className="nav-item">
                      <Link to={SINGLE_MAP.ORDERS.path} className={`nav-link ${isActive(SINGLE_MAP.ORDERS.path) ? "active" : ""}`}>Orders</Link>
                    </li>
                  </ul>
                </div>
              </li>
            )}

            {/* 5. Jobs & Operations */}
            {jobsChildren.length > 0 && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "jobsMenu" ? null : "jobsMenu")} href="#jobsMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "jobsMenu" ? "true" : "false"} aria-controls="jobsMenu">
                  <i className="ri-briefcase-fill" /><span>Jobs & Operations</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "jobsMenu" ? "show" : ""}`} id="jobsMenu">
                  <ul className="nav nav-sm flex-column">
                    {jobsChildren.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* 6. Projects */}
            {has("PROJECTS") && (
              <li className="nav-item">
                <Link
                  to={SINGLE_MAP.PROJECTS.path}
                  className={`nav-link menu-link ${isActive(SINGLE_MAP.PROJECTS.path) ? "active" : ""}`}
                >
                  <i className="ri-folder-chart-line" /><span>Projects</span>
                </Link>
              </li>
            )}

            {/* 7. Studio — matches SignageSidebar: external Studio + Designs when module codes allow */}
            {showStudioMenu && (
              <li className="nav-item">
                <a
                  className="nav-link menu-link"
                  onClick={() => setOpenMenu(openMenu === "studioMenu" ? null : "studioMenu")}
                  href="#studioMenu"
                  data-bs-toggle="collapse"
                  role="button"
                  aria-expanded={openMenu === "studioMenu" ? "true" : "false"}
                  aria-controls="studioMenu"
                >
                  <i className="ri-palette-line" />
                  <span>Studio</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "studioMenu" ? "show" : ""}`} id="studioMenu">
                  <ul className="nav nav-sm flex-column">
                    {showStudioExternal && (
                      <li className="nav-item">
                        <a href={studioUrl} target="_blank" rel="noopener noreferrer" className="nav-link">
                          Studio
                        </a>
                      </li>
                    )}
                    {showDesignsLink && (
                      <li className="nav-item">
                        <Link
                          to={`${baseRoute}/designs`}
                          className={`nav-link ${isActive(`${baseRoute}/designs`) ? "active" : ""}`}
                        >
                          Designs
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* 8. Products & Inventory (Inventory, Catalog Setup, Assets, Warehouses) */}
            {showProductsInventory && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "productsMenu" ? null : "productsMenu")} href="#productsMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "productsMenu" ? "true" : "false"} aria-controls="productsMenu">
                  <i className="ri-store-2-line" /><span>Products & Inventory</span>
                </a>
                <div className={`collapse menu-dropdown ${isParentOpen("productsMenu") ? "show" : ""}`} id="productsMenu">
                  <ul className="nav nav-sm flex-column">
                    {inventorySubChildren.length > 0 && (
                      <li className="nav-item">
                        <a className="nav-link" href="#inventorySub" data-bs-toggle="collapse" role="button" onClick={() => setOpenMenu(openMenu === "inventorySub" ? "productsMenu" : "inventorySub")}>
                          Inventory
                        </a>
                        <div className={`collapse menu-dropdown ${openMenu === "inventorySub" ? "show" : ""}`} id="inventorySub">
                          <ul className="nav nav-sm flex-column">
                            {inventorySubChildren.map((child) => (
                              <li className="nav-item" key={child.path}>
                                <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </li>
                    )}
                    {catalogSetupSubChildren.length > 0 && (
                      <li className="nav-item">
                        <a className="nav-link" href="#catalogSub" data-bs-toggle="collapse" role="button" onClick={() => setOpenMenu(openMenu === "catalogSub" ? "productsMenu" : "catalogSub")}>
                          Catalog Setup
                        </a>
                        <div className={`collapse menu-dropdown ${openMenu === "catalogSub" ? "show" : ""}`} id="catalogSub">
                          <ul className="nav nav-sm flex-column">
                            {catalogSetupSubChildren.map((child) => (
                              <li className="nav-item" key={child.path}>
                                <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </li>
                    )}
                    {has("ASSETS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.ASSETS.path} className={`nav-link ${isActive(SINGLE_MAP.ASSETS.path) ? "active" : ""}`}>Assets</Link>
                      </li>
                    )}
                    {has("WAREHOUSES") && (
                      <li className="nav-item">
                        <Link to={`${baseRoute}/warehouses`} className={`nav-link ${isActive(`${baseRoute}/warehouses`) ? "active" : ""}`}>Warehouses</Link>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* 8. Incoming (separate parent) */}
            {showIncoming && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "incomingMenu" ? null : "incomingMenu")} href="#incomingMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "incomingMenu" ? "true" : "false"} aria-controls="incomingMenu">
                  <i className="ri-arrow-left-down-line" /><span>Incoming</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "incomingMenu" ? "show" : ""}`} id="incomingMenu">
                  <ul className="nav nav-sm flex-column">
                    {incomingChildren.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>
                          {child.icon && <i className={`${child.icon} me-1`} />}{child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* 9. Finance */}
            {(has("BATCH_PAYMENTS") || has("PAYMENT_METHODS") || has("SPECIAL_FEES") || has("DISCOUNTS")) && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "financeMenu" ? null : "financeMenu")} href="#financeMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "financeMenu" ? "true" : "false"} aria-controls="financeMenu">
                  <i className="ri-file-text-line" /><span>Finance</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "financeMenu" ? "show" : ""}`} id="financeMenu">
                  <ul className="nav nav-sm flex-column">
                    {has("BATCH_PAYMENTS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.BATCH_PAYMENTS.path} className={`nav-link ${isActive(SINGLE_MAP.BATCH_PAYMENTS.path) ? "active" : ""}`}>{SINGLE_MAP.BATCH_PAYMENTS.label}</Link>
                      </li>
                    )}
                    {has("PAYMENT_METHODS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.PAYMENT_METHODS.path} className={`nav-link ${isActive(SINGLE_MAP.PAYMENT_METHODS.path) ? "active" : ""}`}>{SINGLE_MAP.PAYMENT_METHODS.label}</Link>
                      </li>
                    )}
                    {has("SPECIAL_FEES") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.SPECIAL_FEES.path} className={`nav-link ${isActive(SINGLE_MAP.SPECIAL_FEES.path) ? "active" : ""}`}>{SINGLE_MAP.SPECIAL_FEES.label}</Link>
                      </li>
                    )}
                    {has("DISCOUNTS") && (
                      <li className="nav-item">
                        <Link to={SINGLE_MAP.DISCOUNTS.path} className={`nav-link ${isActive(SINGLE_MAP.DISCOUNTS.path) ? "active" : ""}`}>{SINGLE_MAP.DISCOUNTS.label}</Link>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* 10. Shipments */}
            {showShipments && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "shipmentsMenu" ? null : "shipmentsMenu")} href="#shipmentsMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "shipmentsMenu" ? "true" : "false"} aria-controls="shipmentsMenu">
                  <Truck className="ms-1 text-white" /><span>Shipments</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "shipmentsMenu" ? "show" : ""}`} id="shipmentsMenu">
                  <ul className="nav nav-sm flex-column">
                    {shipmentsChildren.map((child) => (
                      <li className="nav-item" key={child.path}>
                        <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}

            {/* 11. Integrations */}
            {has("INTEGRATIONS") && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.INTEGRATIONS.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.INTEGRATIONS.path) ? "active" : ""}`}>
                  <i className={SINGLE_MAP.INTEGRATIONS.icon!} /><span className="ms-1">{SINGLE_MAP.INTEGRATIONS.label}</span>
                </Link>
              </li>
            )}

            {/* 13. Configuration (My Organization, Settings) */}
            {(has("ROLES") || has("SIGNAGE_TEAM_MEMBERS") || showSettings) && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "configMenu" ? null : "configMenu")} href="#configMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "configMenu" ? "true" : "false"} aria-controls="configMenu">
                  <i className="ri-settings-4-fill" /><span>Configuration</span>
                </a>
                <div className={`collapse menu-dropdown ${isParentOpen("configMenu") ? "show" : ""}`} id="configMenu">
                  <ul className="nav nav-sm flex-column">
                    {(has("ROLES") || has("SIGNAGE_TEAM_MEMBERS")) && (
                      <li className="nav-item">
                        <a className="nav-link" href="#myOrgSub" data-bs-toggle="collapse" role="button" onClick={() => setOpenMenu(openMenu === "myOrgSub" ? "configMenu" : "myOrgSub")}>
                          My Organization
                        </a>
                        <div className={`collapse menu-dropdown ${openMenu === "myOrgSub" ? "show" : ""}`} id="myOrgSub">
                          <ul className="nav nav-sm flex-column">
                            {has("ROLES") && (
                              <li className="nav-item">
                                <Link to={`${baseRoute}/roles`} className={`nav-link ${isActive(`${baseRoute}/roles`) ? "active" : ""}`}>Roles & Permissions</Link>
                              </li>
                            )}
                            {/* {has("SIGNAGE_TEAM_MEMBERS") && (
                              <li className="nav-item">
                                <Link to={`${baseRoute}/teammembers`} className={`nav-link ${isActive(`${baseRoute}/teammembers`) || isActive(`${baseRoute}/SignageTeammember`) ? "active" : ""}`}>Team Members</Link>
                              </li>
                            )} */}
                          </ul>
                        </div>
                      </li>
                    )}
                    {showSettings && (
                      <li className="nav-item">
                        <a className="nav-link" href="#settingsSub" data-bs-toggle="collapse" role="button" onClick={() => setOpenMenu(openMenu === "settingsSub" ? "configMenu" : "settingsSub")}>
                          Settings
                        </a>
                        <div className={`collapse menu-dropdown ${isParentOpen("settingsSub") ? "show" : ""}`} id="settingsSub">
                          <ul className="nav nav-sm flex-column">
                            {settingsChildren.map((child) =>
                              child.subchildren ? (
                                <li className="nav-item" key={child.path}>
                                  <a className="nav-link" href="#taxSub" data-bs-toggle="collapse" role="button" onClick={() => setOpenMenu(openMenu === "taxSub" ? "settingsSub" : "taxSub")}>
                                    {child.label}
                                  </a>
                                  <div className={`collapse menu-dropdown ${openMenu === "taxSub" ? "show" : ""}`} id="taxSub">
                                    <ul className="nav nav-sm flex-column">
                                      {child.subchildren.map((sub) => (
                                        <li className="nav-item" key={sub.path}>
                                          <Link to={sub.path} className={`nav-link ${isActive(sub.path) ? "active" : ""}`}>{sub.label}</Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </li>
                              ) : (
                                <li className="nav-item" key={child.path}>
                                  <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

            {/* 13. Marketplace */}
            {has("MARKETPLACE_PROFILE") && (
              <li className="nav-item">
                <a className="nav-link menu-link" onClick={() => setOpenMenu(openMenu === "marketplaceMenu" ? null : "marketplaceMenu")} href="#marketplaceMenu" data-bs-toggle="collapse" role="button" aria-expanded={openMenu === "marketplaceMenu" ? "true" : "false"} aria-controls="marketplaceMenu">
                  <i className="ri-profile-line" /><span>Marketplace</span>
                </a>
                <div className={`collapse menu-dropdown ${openMenu === "marketplaceMenu" ? "show" : ""}`} id="marketplaceMenu">
                  <ul className="nav nav-sm flex-column">
                    <li className="nav-item">
                      <Link to={SINGLE_MAP.MARKETPLACE_PROFILE.path} className={`nav-link ${isActive(SINGLE_MAP.MARKETPLACE_PROFILE.path) ? "active" : ""}`}>Marketplace Profile</Link>
                    </li>
                  </ul>
                </div>
              </li>
            )}

            {/* 14. Post Types (below Marketplace; backend may return POST_TYPES or POST_TYPE) */}
            {(has("POST_TYPES") || has("POST_TYPE")) && (
              <li className="nav-item">
                <Link to={SINGLE_MAP.POST_TYPES.path} className={`nav-link menu-link ${isActive(SINGLE_MAP.POST_TYPES.path) ? "active" : ""}`}>
                  <Boxes size={16} style={{ verticalAlign: "middle" }} />
                  <span className="ms-1">Post Types</span>
                </Link>
              </li>
            )}

            {/* 15. Profile */}
            <li className="nav-item">
              <Link to={`${baseRoute}/profile`} className={`nav-link menu-link ${isActive(`${baseRoute}/profile`) ? "active" : ""}`}>
                <i className="ri-user-line" /><span className="ms-1">Profile</span>
              </Link>
            </li>

            {/* 15) Logout (static) */}
            <li className="nav-item">
              <LogoutButton />
            </li>
          </ul>
        </div>
      </div>

      <div
        className="sidebar-background"
        onClick={() => {
          document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
        }}
      />
    </div>
  );
}














// // Helper function to create settings children based on baseRoute
// const createSettingsChildren = (baseRoute: string): ItemMeta[] => [
//   { label: "Invoice templates", path: `${baseRoute}/InvoiceTemplates` },
//   { label: "Dynamic costs", path: `${baseRoute}/dynamic-costs` },
//   { label: "Shipping Cost", path: `${baseRoute}/shipping/shipping-costs` },
//   { label: "Overdue Trigger Settings", path: `${baseRoute}/overdueSettings` },
// // Settings child with module code for permission check (SETTINGS module not sent by backend - always show dropdown, filter children by their modules)
// type SettingsChildMeta = ItemMeta & { moduleCode?: ModuleCode };
// const createSettingsChildren = (baseRoute: string): SettingsChildMeta[] => [
//   { label: "Invoice templates", path: `${baseRoute}/InvoiceTemplates`, moduleCode: "INVOICE_TEMPLATES" },
//   { label: "Dynamic costs", path: `${baseRoute}/dynamic-costs`, moduleCode: "DYNAMIC_COSTS" },
//   { label: "Shipping Cost", path: `${baseRoute}/shipping/shipping-costs`, moduleCode: "SHIPPING_METHODS_COST" },
//   { label: "Overdue Trigger Settings", path: `${baseRoute}/overdueSettings`, moduleCode: "OVERDUE_TRIGGER_CONFIGURATION" },
// ];



// 210


//   // Settings: backend does not send SETTINGS module - always show dropdown (default), filter children by their module codes
//   const settingsChildren = useMemo(
//     () =>
//       SETTINGS_CHILDREN.filter(
//         (child) => !child.moduleCode || has(child.moduleCode as ModuleCode)
//       ),
//     [codes, SETTINGS_CHILDREN]
//   );
//   const showSettings = settingsChildren.length > 0; // No SETTINGS module from backend - show when user has any settings child permission

//   const showIncoming = has("INCOMING_PRODUCTS") || has("LINKED_INSTALLER") || incomingChildren.length > 0;
//   const showTax = has("TAX_SETTINGS");
// const showSettings = has("SETTINGS");
//   const showShipments = has("SHIPMENTS") || has("SHIPPING_PRESETS") || has("BUY_LABELS") || has("POSTAGE_WALLET");


//   {SETTINGS_CHILDREN.map((child) => (


//  {settingsChildren.map((child) => (
//                       <li className="nav-item" key={child.path}>
//                         <Link to={child.path} className={`nav-link ${isActive(child.path) ? "active" : ""}`}>{child.label}</Link>
//                       </li>
//                     ))}